import type { EventMetadata } from './BaseEvent.js';
import type { Severity, Breadcrumb } from '../types.js';

/**
 * Error event types - flat structure for ClickHouse
 */
export type ErrorEventType = 
  | 'javascript_error'
  | 'network_error'
  | 'resource_error'
  | 'promise_rejection';

/**
 * Error event optimized for ClickHouse columnar storage
 */
export interface ErrorEvent extends EventMetadata {
  event_category: 'error';
  event_type: ErrorEventType;

  // Error details (all flat - no nesting)
  error_message: string;
  error_stack: string | null;
  error_severity: Severity;        // LowCardinality
  error_filename: string | null;
  error_line: number | null;       // Nullable(UInt32)
  error_column: number | null;     // Nullable(UInt32)
  
  // Additional context
  breadcrumbs_json: string;        // JSON string of breadcrumbs array
  
  // For network/resource errors
  resource_url: string | null;
  resource_type: string | null;    // 'script' | 'stylesheet' | 'image' etc.
  http_status: number | null;      // Nullable(UInt16)
  http_status_text: string | null;
}

/**
 * Create error event from Error object
 */
export function createErrorEvent(
  baseMetadata: EventMetadata,
  error: Error | string,
  type: ErrorEventType,
  severity: Severity,
  breadcrumbs: Breadcrumb[],
  additionalContext?: {
    filename?: string;
    lineno?: number;
    colno?: number;
    resourceUrl?: string;
    resourceType?: string;
    httpStatus?: number;
    httpStatusText?: string;
  }
): ErrorEvent {
  const errorMessage = typeof error === 'string' ? error : error.message;
  const errorStack = error instanceof Error ? error.stack || null : null;

  return {
    ...baseMetadata,
    event_category: 'error',
    event_type: type,
    error_message: truncateString(errorMessage, 500),
    error_stack: errorStack ? truncateString(errorStack, 1000) : null,
    error_severity: severity,
    error_filename: additionalContext?.filename || null,
    error_line: additionalContext?.lineno || null,
    error_column: additionalContext?.colno || null,
    breadcrumbs_json: JSON.stringify(breadcrumbs),
    resource_url: additionalContext?.resourceUrl || null,
    resource_type: additionalContext?.resourceType || null,
    http_status: additionalContext?.httpStatus || null,
    http_status_text: additionalContext?.httpStatusText || null
  };
}

function truncateString(str: string, maxLength: number): string {
  return str.length > maxLength ? str.substring(0, maxLength) + '...' : str;
}

